import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  approveCalculation,
  CALC_KEY,
  CASES_KEY,
  ProblemError,
  setCaseStatus,
  withdrawApproval,
  type ApproveIn,
  type CalculationOut,
} from "@/api";

import { refetchCase } from "./refetchCase";

function isNonEmptyStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === "string");
}

/**
 * The 409 on `approve`/`withdraw`/`/calculations` carries `ConflictOut`
 * (`apps/api/app/routes/calculation.py::_conflict`, `workflow/router.py`):
 * `{ detail: string, reasons: string[], latestVersionNo: number | null }`.
 * `detail` is always the one joined sentence; the per-reason list — what
 * `pricing.approvable` actually found — lives in `reasons` (controller notes
 * §1; corrects the brief's assumption of an array under `detail`). The
 * client's `toProblemDetails` keeps the whole body in `error.problem.raw`, so
 * it is read from there rather than from `problem.detail`. A non-empty
 * `reasons` array wins; otherwise the one sentence is shown as the one
 * reason. Anything that is not a 409 is not a refusal and gets `null` — the
 * screen shows those as a plain line instead.
 */
export function refusalReasons(error: unknown): string[] | null {
  if (!(error instanceof ProblemError) || error.status !== 409) return null;
  const raw = error.problem.raw;
  const reasons = typeof raw === "object" && raw !== null ? (raw as { reasons?: unknown }).reasons : undefined;
  if (isNonEmptyStringArray(reasons)) return reasons;
  return [error.userMessage];
}

/**
 * The three human gestures on a calculation (E-52): approve, withdraw, mark
 * the quote as sent. `approve` and `withdraw` answer with the calculation
 * and its review — written straight into the cache, no second GET for it;
 * all three move the Vorgang's status, so the Vorgang (exact key — not the
 * prefix, or the entry just written would be refetched) and the start
 * page's list are invalidated. A refused approval invalidates the
 * calculation too: the server may have found what this browser did not see
 * yet, a changed price state above all.
 *
 * **The answer goes in only once the Vorgang has been read again** (Task
 * 13f, as `useRunCalculation` since 13b): written first, a release stood as
 * „Freigegeben" without „Angebot raus" and „Freigabe zurückziehen" — the
 * gestures wait for `quote_approved` — and a withdrawal showed the approval
 * form on a Vorgang the server still called released, both for the length
 * of the refetch. Until then the gesture stays pending — at most
 * `CASE_REFETCH_WAIT_MS` (review 13/14, M-1); a refetch that fails or never
 * answers does not hold the answer back.
 */
export function useApproval(caseId: string, calcId: string) {
  const client = useQueryClient();

  async function settle(next: CalculationOut): Promise<void> {
    void client.invalidateQueries({ queryKey: CASES_KEY });
    await refetchCase(client, caseId);
    client.setQueryData(CALC_KEY(caseId), next);
  }

  const approve = useMutation({
    mutationFn: (input: ApproveIn) => approveCalculation(caseId, calcId, input),
    onSuccess: settle,
    onError: () => {
      void client.invalidateQueries({ queryKey: CALC_KEY(caseId) });
    },
  });

  // Fix round 2, item 12: a 409 here (a colleague moved the case, or the
  // calculation, on) used to leave both `detail` and `calc` exactly as
  // stale as they were before the attempt — the two buttons stayed on offer
  // and the next click failed the same way. `approve`'s own `onError`
  // already invalidates the calculation; both mirror it and also
  // invalidate the case, since either mutation's own conflict can turn on
  // the Vorgang's status having moved (`ApprovalPanel`'s `canGesture`).
  const withdraw = useMutation({
    mutationFn: (note: string) => withdrawApproval(caseId, calcId, { note }),
    onSuccess: settle,
    onError: () => {
      void client.invalidateQueries({ queryKey: CALC_KEY(caseId) });
      void client.invalidateQueries({ queryKey: ["case", caseId], exact: true });
    },
  });

  const send = useMutation({
    mutationFn: () => setCaseStatus(caseId, "sent"),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["case", caseId], exact: true });
      void client.invalidateQueries({ queryKey: CASES_KEY });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: CALC_KEY(caseId) });
      void client.invalidateQueries({ queryKey: ["case", caseId], exact: true });
    },
  });

  return { approve, withdraw, send };
}
